import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client from "@/lib/api";

const BrandContext = createContext(null);

const DEFAULTS = {
  brand_name: "Dealer Network Platform",
  brand_logo_url: null,
  brand_primary_color: null,
  brand_secondary_color: null,
};

function hexToHsl(hex) {
  const clean = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await client.get(`/settings-public/branding`);
      setBrand({ ...DEFAULTS, ...data });
    } catch (err) {
      console.warn("BrandContext.refresh failed:", err);
      setBrand(DEFAULTS);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const root = document.documentElement.style;
    const hsl = brand.brand_primary_color ? hexToHsl(brand.brand_primary_color) : null;
    if (hsl) {
      root.setProperty("--brand-accent", brand.brand_primary_color);
      root.setProperty("--primary", `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      root.setProperty("--ring", `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      root.setProperty("--primary-foreground", hsl.l > 55 ? "240 6% 10%" : "0 0% 100%");
    } else {
      root.removeProperty("--brand-accent");
      root.removeProperty("--primary");
      root.removeProperty("--ring");
      root.removeProperty("--primary-foreground");
    }
  }, [brand.brand_primary_color]);

  useEffect(() => {
    const root = document.documentElement.style;
    if (brand.brand_secondary_color) {
      root.setProperty("--brand-secondary-accent", brand.brand_secondary_color);
    } else {
      root.removeProperty("--brand-secondary-accent");
    }
  }, [brand.brand_secondary_color]);

  const value = useMemo(() => ({ brand, refresh }), [brand, refresh]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error("useBrand must be used within BrandProvider");
  return ctx;
}
